// Journal pop-over — Tate's #5 (Journal/Margins), day-0.1.
//
// Pops OVER the script (not modal, not separate route) — a warm-paper
// overlay that rises from the bottom so the actor stays anchored to the line.
// Holds the actor's own private work: objective, beat, given circumstances,
// substitution, note. Cael's prompts as OPEN QUESTIONS, never imperatives.
//
// Private by default. The "Held" confirm is the keeper-not-source landing.
import { useEffect, useState } from "react";
import { Annotation, getAnnotations, postAnnotation } from "../api/client";

const PROMPTS: Record<string, string> = {
  objective:           "What's your scene objective here?",
  beat:                "What does this beat want?",
  given_circumstances: "What's true in this moment — that you'd inhabit, not perform?",
  substitution:        "Who in your real life makes this line land?",
  note:                "What do you want to remember about this line?",
  idea:                "Catch it here.",
};

const EMPTY_FALLBACK: Record<string, string> = {
  objective:           "No objective yet.",
  beat:                "No beat yet.",
  given_circumstances: "Nothing here yet.",
  substitution:        "Nothing here yet.",
  note:                "No note yet.",
  idea:                "No ideas yet.",
};

import type { MethodologyPrompt } from "../api/client";

export interface JournalOverlayProps {
  projectId:  string;
  lineId:     number;
  // Widened from Annotation["kind"] union to plain string so methodology-
  // specific kinds (Chubbuck's scene_objective / inner_objects / etc.) can
  // be passed through from the per-methodology prompt sequence. Backend
  // validates against the full kinds enum.
  kind:       string;
  prompt?:    MethodologyPrompt | null;     // the full prompt object for headline + text
  allPrompts?: MethodologyPrompt[];          // all Scene Breakdown prompts — sidebar
  onClose:   () => void;
}

export default function JournalOverlay({
  projectId, lineId, kind, prompt, allPrompts, onClose,
}: JournalOverlayProps) {
  const [body, setBody] = useState("");
  const [held, setHeld] = useState(false);
  const [existing, setExisting] = useState<Annotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeKind, setActiveKind] = useState(kind);
  const [activePrompt, setActivePrompt] = useState<MethodologyPrompt | null>(prompt ?? null);

  useEffect(() => {
    let cancelled = false;
    getAnnotations(projectId)
      .then((res) => {
        if (cancelled) return;
        const here = res.annotations.filter((a) => a.kind === activeKind);
        setExisting(here);
      })
      .catch(() => { /* silent */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, activeKind]);

  async function save() {
    if (!body.trim()) return;
    try {
      const saved = await postAnnotation({
        project_id: projectId,
        kind: activeKind as Annotation["kind"],
        body,
        line_range: [lineId, lineId],
        scene_idx: null,
        media_url: null,
      });
      setExisting((prev) => [saved, ...prev]);
      setBody("");
      setHeld(true);
      window.setTimeout(() => setHeld(false), 2000);
    } catch (e) { /* silent for v0.1 */ }
  }

  function switchPrompt(p: MethodologyPrompt) {
    setActiveKind(p.kind);
    setActivePrompt(p);
    setBody("");
  }

  // Headline question: prefer the spec's prompt text when provided,
  // otherwise fall back to the static map for ad-hoc journal opens.
  const headlineText = activePrompt?.text ?? PROMPTS[activeKind] ?? activeKind;
  const headlineLabel = activePrompt?.label ?? activeKind.replace(/_/g, " ");

  return (
    <div className="v2-journal-overlay v2">
      <div className="v2-journal-handle" onClick={onClose} role="button" />

      <div style={{ display: "flex", alignItems: "baseline", gap: "0.6rem", marginBottom: "0.4rem" }}>
        {activePrompt?.tool_number !== undefined && activePrompt?.tool_number !== null && (
          <span className="v2-tracked-caps" style={{ color: "var(--v2-gold)" }}>
            #{activePrompt.tool_number}
          </span>
        )}
        <span className="v2-tracked-caps">{headlineLabel}</span>
        {activePrompt?.private && (
          <span className="v2-tracked-caps" style={{ color: "var(--v2-gold)", fontSize: "0.55rem" }}>
            private
          </span>
        )}
      </div>

      <p className="v2-invitation" style={{ fontSize: "1.3rem", textAlign: "left", margin: "0.2rem 0 1.2rem", lineHeight: 1.35 }}>
        {headlineText}
      </p>

      <textarea
        className="v2-input"
        rows={4}
        placeholder={existing.length ? "" : "…"}
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "1rem" }}>
        <button className="v2-affordance" onClick={onClose}>Close</button>
        <button className="v2-affordance" onClick={save} disabled={!body.trim()}>
          {held ? "Held" : "Hold"}
        </button>
      </div>

      <hr className="v2-hairline" />

      {!loading && existing.length === 0 && (
        <p className="v2-prompt" style={{ color: "var(--v2-ink-faint)" }}>
          {EMPTY_FALLBACK[activeKind] ?? "Nothing here yet."}
        </p>
      )}

      {existing.map((a) => (
        <div key={a.id} style={{ padding: "0.7rem 0", borderBottom: "1px solid var(--v2-hairline)" }}>
          <p className="v2-tracked-caps" style={{ fontSize: "0.6rem" }}>
            {new Date(a.ts_utc).toLocaleString()}
          </p>
          <p style={{ fontFamily: "var(--v2-serif)", margin: "0.2rem 0" }}>{a.body}</p>
        </div>
      ))}

      {allPrompts && allPrompts.length > 0 && (
        <>
          <hr className="v2-hairline" />
          <p className="v2-tracked-caps" style={{ marginBottom: "0.6rem" }}>The whole sequence</p>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {allPrompts.map((p) => {
              const isActive = p.kind === activeKind;
              return (
                <li
                  key={`${p.tool_number}-${p.kind}`}
                  onClick={() => switchPrompt(p)}
                  style={{
                    padding: "0.55rem 0",
                    borderBottom: "1px solid var(--v2-hairline)",
                    cursor: "pointer",
                    display: "flex",
                    gap: "0.7rem",
                    alignItems: "baseline",
                    opacity: isActive ? 1 : 0.7,
                  }}
                >
                  {p.tool_number !== null && (
                    <span className="v2-tracked-caps" style={{ color: isActive ? "var(--v2-gold)" : "var(--v2-ink-faint)", minWidth: "1.6rem" }}>
                      #{p.tool_number}
                    </span>
                  )}
                  <span style={{ fontFamily: "var(--v2-serif)", fontStyle: "italic", color: "var(--v2-ink)" }}>
                    {p.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}

      <p className="v2-prompt" style={{ fontSize: "0.85rem", color: "var(--v2-ink-faint)", textAlign: "center", marginTop: "1.5rem" }}>
        Private. Yours.
      </p>
    </div>
  );
}
